import { pipelineHandoffRulesAPI } from '../../api/kanban';
import { kanbanErrorMessage } from './kanbanApiError';

/**
 * `rules` vazio ≠ "esta conta não tem passagem".
 *
 * A lista nasce vazia e continua vazia se o GET quebrar — e uma tela que
 * desenha "nenhuma passagem sai deste funil" em cima de um GET quebrado está
 * AFIRMANDO que nenhum card vai embora sozinho, que é o oposto do que pode
 * estar acontecendo. `loaded` e `loadError` preservam a diferença; é a mesma
 * disciplina do `byStageId` das automações de estágio.
 */
export const state = {
  rules: [],
  loaded: false,
  loadError: null,
  uiFlags: {
    isFetching: false,
    isSaving: false,
    isDeleting: false,
  },
};

export const getters = {
  rules: _state => _state.rules,
  isLoaded: _state => _state.loaded,
  loadError: _state => _state.loadError,
  uiFlags: _state => _state.uiFlags,
  ruleById: _state => id => _state.rules.find(r => r.id === Number(id)),
};

export const actions = {
  async fetch({ commit }) {
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const { data } = await pipelineHandoffRulesAPI.list();
      commit('SET_RULES', data);
      return data;
    } catch (error) {
      // Marca e PROPAGA: quem espera o resultado decide, quem chamou por
      // consequência (o `deleteStage`/`deletePipeline`) engole sabendo que o
      // erro fica registrado aqui e a aba Passagens o mostra.
      commit('SET_LOAD_ERROR', kanbanErrorMessage(error));
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isFetching: false });
    }
  },

  async create({ commit }, payload) {
    commit('SET_UI_FLAG', { isSaving: true });
    try {
      const { data } = await pipelineHandoffRulesAPI.create(payload);
      commit('UPSERT_RULE', data);
      return data;
    } finally {
      commit('SET_UI_FLAG', { isSaving: false });
    }
  },

  async update({ commit }, { id, payload }) {
    commit('SET_UI_FLAG', { isSaving: true });
    try {
      const { data } = await pipelineHandoffRulesAPI.update(id, payload);
      commit('UPSERT_RULE', data);
      return data;
    } finally {
      commit('SET_UI_FLAG', { isSaving: false });
    }
  },

  async delete({ commit }, id) {
    commit('SET_UI_FLAG', { isDeleting: true });
    try {
      await pipelineHandoffRulesAPI.delete(id);
      commit('REMOVE_RULE', id);
    } finally {
      commit('SET_UI_FLAG', { isDeleting: false });
    }
  },
};

export const mutations = {
  SET_UI_FLAG: (_state, flags) => {
    _state.uiFlags = { ..._state.uiFlags, ...flags };
  },
  SET_RULES: (_state, rules) => {
    _state.rules = rules;
    _state.loaded = true;
    _state.loadError = null;
  },
  SET_LOAD_ERROR: (_state, message) => {
    _state.loadError = message;
    _state.loaded = false;
  },
  UPSERT_RULE: (_state, rule) => {
    const idx = _state.rules.findIndex(r => r.id === rule.id);
    if (idx >= 0) {
      _state.rules = [
        ..._state.rules.slice(0, idx),
        rule,
        ..._state.rules.slice(idx + 1),
      ];
    } else {
      _state.rules = [...(_state.rules || []), rule];
    }
  },
  REMOVE_RULE: (_state, id) => {
    _state.rules = _state.rules.filter(r => r.id !== id);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
